import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Radar, Trophy, TrendingUp, TrendingDown, Activity, Calendar, BarChart3, Clock } from 'lucide-react';
import { BOT_CONFIG } from './MTModule';
import TradeStreamLive from './TradeStreamLive';
import { apiFetch } from '../utils/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * AI Trading Dashboard — TWO blocks:
 *   A) Bot Battle Board   — aggregated per-bot leaderboard with KPIs, score & sparkline
 *   B) Risk Radar 2030    — 5-axis polar radar overlaying all bots
 *
 * Input: details = [{ ai_bot, info, analytics, login, ... }, ...]
 * No data → renders an empty placeholder.
 */

const fmtUsd = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtUsdShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

// Deterministic random walk seeded by login → synthetic 30-point equity curve
const sparklinePath = (loginSeed, finalValue, width, height, points = 30) => {
  let s = 0;
  const seedStr = String(loginSeed || 'seed');
  for (let i = 0; i < seedStr.length; i++) s = (s * 31 + seedStr.charCodeAt(i)) % 2147483647;
  const rand = () => {
    s = (s * 1103515245 + 12345) % 2147483647;
    return (s % 10000) / 10000;
  };
  const data = [];
  let v = finalValue * (0.7 + rand() * 0.15);
  for (let i = 0; i < points - 1; i++) {
    v *= 1 + (rand() - 0.45) * 0.04;
    data.push(v);
  }
  data.push(finalValue); // end exactly at current equity
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  return data.map((d, i) => {
    const x = (i / (points - 1)) * width;
    const y = height - ((d - min) / range) * height;
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
};

// Performance score 0-100 from analytics
const performanceScore = (a) => {
  if (!a || !a.trades) return 0;
  const wr  = Math.min(100, Math.max(0, a.winRate || 0));
  const pf  = Math.min(3, Math.max(0, a.profitFactor || 0)) / 3 * 100;
  const sh  = Math.min(3, Math.max(0, a.sharpeRatio || 0)) / 3 * 100;
  const dd  = Math.max(0, 100 - (a.maxDrawdown || 0) * 4);
  return Math.round(wr * 0.30 + pf * 0.30 + sh * 0.20 + dd * 0.20);
};

// Aggregate stats per bot, ignoring custom (we only spotlight Sigmai / Sugair / Jairvis)
const aggregateByBot = (details) => {
  const groups = { sigmai: [], sugair: [], jairvis: [] };
  details.forEach((d) => {
    if (groups[d.ai_bot]) groups[d.ai_bot].push(d);
  });
  return Object.entries(groups).map(([key, accs]) => {
    let equity = 0, profit = 0, trades = 0, won = 0, lost = 0;
    let pfSum = 0, shSum = 0, ddMax = 0, pfN = 0, shN = 0;
    let pfAllWins = false, pfPending = false, sharpePending = false, ddPending = false;
    accs.forEach((a) => {
      equity += a.info?.equity || 0;
      profit += a.info?.profit || 0;
      trades += a.analytics?.trades || 0;
      won    += a.analytics?.wonTrades || 0;
      lost   += a.analytics?.lostTrades || 0;
      if (a.analytics?.profitFactor) { pfSum += a.analytics.profitFactor; pfN++; }
      if (a.analytics?.sharpeRatio)  { shSum += a.analytics.sharpeRatio;  shN++; }
      if (a.analytics?.maxDrawdown)  { ddMax = Math.max(ddMax, a.analytics.maxDrawdown); }
      if (a.analytics?._pf_all_wins)     pfAllWins = true;
      if (a.analytics?._pf_pending)      pfPending = true;
      if (a.analytics?._sharpe_pending)  sharpePending = true;
      if (a.analytics?._dd_pending)      ddPending = true;
    });
    const analytics = {
      trades, wonTrades: won, lostTrades: lost,
      winRate: trades > 0 ? (won / trades) * 100 : 0,
      profitFactor: pfN > 0 ? pfSum / pfN : 0,
      sharpeRatio:  shN > 0 ? shSum / shN  : 0,
      maxDrawdown: ddMax,
      _pfAllWins: pfAllWins,
      _pfPending: pfPending,
      _sharpePending: sharpePending,
      _ddPending: ddPending,
    };
    const score = performanceScore(analytics);
    return {
      key,
      ...BOT_CONFIG[key],
      accounts: accs.length,
      seed: accs.map((a) => a.login).join('-') || key,
      equity, profit, analytics, score,
    };
  });
};

/* ─────────────────────── A) Bot Battle Board ─────────────────────── */

const BotColumn = ({ bot, animateIn }) => {
  const a = bot.analytics;
  const sparkW = 220, sparkH = 50;
  const sparkPath = sparklinePath(bot.seed, bot.equity || 1, sparkW, sparkH);
  return (
    <div
      className="relative rounded-2xl p-4 overflow-hidden transition-transform hover:-translate-y-1 duration-300"
      style={{
        background: `linear-gradient(165deg, ${bot.color}18 0%, rgba(4,8,22,0.85) 60%)`,
        border: `1px solid ${bot.color}55`,
        boxShadow: `0 12px 30px -15px ${bot.glow}`,
      }}
      data-testid={`battle-col-${bot.key}`}
    >
      {/* Scan-line overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
        style={{
          backgroundImage: 'repeating-linear-gradient(180deg, transparent 0px, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 3px)',
        }}
      />

      {/* Header — logo orb + name */}
      <div className="flex items-center gap-3 relative z-10">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: `radial-gradient(circle, ${bot.color}25, ${bot.color}05)`,
            border: `1.5px solid ${bot.color}`,
            boxShadow: `0 0 18px -2px ${bot.glow}`,
            animation: 'sim360-bot-pulse 2.4s ease-in-out infinite',
          }}
        >
          {bot.logo ? (
            <img src={bot.logo} alt={bot.label} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <span style={{ color: bot.color, fontWeight: 800 }}>{bot.label?.[0]}</span>
          )}
        </div>
        <div>
          <div
            className="text-xl font-black tracking-tight"
            style={{ color: bot.color, textShadow: `0 0 10px ${bot.glow}` }}
          >
            {bot.label}
          </div>
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#9EB4C0]">
            {bot.accounts > 0
              ? `${bot.accounts} acc · ${a.trades} trades`
              : (a.trades > 0 ? `core · ${a.trades} trades` : 'no data')}
          </div>
        </div>
      </div>

      {/* Big equity + profit */}
      <div className="mt-4 relative z-10">
        <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475]">
          {bot.accounts > 0 ? 'Your Equity' : 'Equity'}
        </div>
        <div className="text-2xl font-black" style={{ color: '#fff', fontFamily: 'ui-monospace, monospace' }}>
          {bot.accounts > 0 ? fmtUsdShort(bot.equity) : <span className="text-[#556475]">— hidden</span>}
        </div>
        {bot.accounts > 0 ? (
          <div className={`text-[11px] flex items-center gap-1 mt-0.5 ${bot.profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
            {bot.profit >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {bot.profit >= 0 ? '+' : ''}{fmtUsdShort(bot.profit)} <span className="text-[#556475]">P/L</span>
          </div>
        ) : (
          <div className="text-[10px] mt-0.5 text-[#556475] italic">connect your account to see balance</div>
        )}
      </div>

      {/* Sparkline */}
      {bot.accounts > 0 && (
        <svg viewBox={`0 0 ${sparkW} ${sparkH}`} className="w-full h-12 mt-3 relative z-10" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`spark-${bot.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bot.color} stopOpacity="0.5" />
              <stop offset="100%" stopColor={bot.color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${sparkPath} L ${sparkW} ${sparkH} L 0 ${sparkH} Z`} fill={`url(#spark-${bot.key})`} />
          <path d={sparkPath} fill="none" stroke={bot.color} strokeWidth="1.5"
            style={{
              filter: `drop-shadow(0 0 4px ${bot.glow})`,
              strokeDasharray: 600,
              strokeDashoffset: animateIn ? 0 : 600,
              transition: 'stroke-dashoffset 1.4s ease-out',
            }} />
        </svg>
      )}

      {/* 4 KPI grid */}
      <div className="grid grid-cols-2 gap-2 mt-3 relative z-10">
        <Kpi label="Win Rate"      value={`${a.winRate.toFixed(1)}%`}   accent={bot.color} pct={a.winRate} />
        <Kpi
          label="Profit Factor"
          value={a._pfAllWins ? '∞' : (a._pfPending ? '—' : a.profitFactor.toFixed(2))}
          accent={bot.color}
          highlight={a.profitFactor >= 1.5 || a._pfAllWins}
          title={a._pfAllWins ? 'All trades won — Profit Factor is mathematically infinite' : (a._pfPending ? 'MetaStats still calculating — check MyFxBook for full-history value' : null)}
        />
        <Kpi
          label="Sharpe"
          value={a._sharpePending ? '—' : a.sharpeRatio.toFixed(2)}
          accent={bot.color}
          highlight={a.sharpeRatio >= 1}
          title={a._sharpePending ? 'Sharpe needs ≥30 days of equity history — calibrating' : null}
        />
        <Kpi
          label="Max DD"
          value={a._ddPending ? '—' : `${a.maxDrawdown.toFixed(1)}%`}
          accent={bot.color}
          negative={a.maxDrawdown > 15}
          title={a._ddPending ? 'Max drawdown still being computed by MetaStats' : null}
        />
      </div>

      {/* Health bar — individual readiness, NOT a leaderboard rank */}
      <div className="mt-4 relative z-10">
        <div className="flex items-center justify-between text-[9px] tracking-[0.3em] uppercase">
          <span className="text-[#9EB4C0]">Health</span>
          <span style={{ color: bot.color, textShadow: `0 0 8px ${bot.glow}` }} className="font-black text-sm tabular-nums">
            {bot.score}/100
          </span>
        </div>
        <div className="h-1.5 mt-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: animateIn ? `${bot.score}%` : '0%',
              background: `linear-gradient(90deg, ${bot.color} 0%, #fff 100%)`,
              boxShadow: `0 0 8px ${bot.glow}`,
              transition: 'width 1.6s cubic-bezier(.2,.8,.2,1)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, accent, pct, highlight, negative, title }) => {
  let color = '#fff';
  if (highlight) color = '#22c55e';
  if (negative)  color = '#ef4444';
  return (
    <div className="rounded-lg px-2 py-1.5" title={title || undefined}
      style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${accent}22`, cursor: title ? 'help' : 'default' }}>
      <div className="text-[8px] tracking-[0.25em] uppercase text-[#556475]">{label}</div>
      <div className="text-sm font-bold mt-0.5 tabular-nums" style={{ color, fontFamily: 'ui-monospace, monospace' }}>
        {value}
      </div>
      {pct !== undefined && (
        <div className="h-0.5 mt-1 bg-white/5 rounded">
          <div className="h-full rounded" style={{ width: `${Math.min(100, pct)}%`, background: color, boxShadow: `0 0 4px ${color}` }} />
        </div>
      )}
    </div>
  );
};

const BotBattleBoard = ({ bots, animateIn }) => (
  <Card
    className="overflow-hidden border"
    style={{
      background: 'linear-gradient(180deg, rgba(0,244,255,0.04), rgba(4,8,22,0.85))',
      borderColor: 'rgba(0,244,255,0.25)',
      boxShadow: '0 18px 60px -25px rgba(0,244,255,0.35)',
    }}
    data-testid="bot-battle-board"
  >
    <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,244,255,0.18)' }}>
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-[#00f4ff]" />
        <h3 className="text-base font-bold text-white">AI Synergy Trio</h3>
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#00f4ff]">3 cores · 1 mission</span>
      </div>
      <div className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">Diversified · complementary</div>
    </div>
    <CardContent className="p-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {bots.map((b) => (
          <BotColumn key={b.key} bot={b} animateIn={animateIn} />
        ))}
      </div>
      <p className="mt-4 text-[10px] text-[#556475] italic text-center">
        Each Health score is computed independently — they do not compete with each other.
        Health = WinRate ·30% + ProfitFactor ·30% + Sharpe ·20% + (1−DD) ·20%. Sparkline = synthetic 30-pt equity curve illustrative.
      </p>
    </CardContent>
  </Card>
);

/* ─────────────────────── B) Risk Radar 2030 ─────────────────────── */

const radarAxes = [
  { key: 'winRate',      label: 'WIN RATE',     fn: (a) => Math.min(100, a.winRate || 0) },
  { key: 'profitFactor', label: 'PROFIT FACT.', fn: (a) => Math.min(100, ((a.profitFactor || 0) / 3) * 100) },
  { key: 'sharpe',       label: 'SHARPE',       fn: (a) => Math.min(100, ((a.sharpeRatio || 0) / 3) * 100) },
  { key: 'risk',         label: '1 / DD',       fn: (a) => Math.max(0, 100 - (a.maxDrawdown || 0) * 4) },
  { key: 'activity',     label: 'ACTIVITY',     fn: (a) => Math.min(100, ((a.trades || 0) / 200) * 100) },
];

const polar = (cx, cy, radius, angle) => {
  const a = angle - Math.PI / 2; // start at top
  return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)];
};

const RiskRadar2030 = ({ bots, animateIn }) => {
  const size = 360, cx = size / 2, cy = size / 2, maxR = size / 2 - 38;
  const n = radarAxes.length;
  const rings = [25, 50, 75, 100];

  const insights = useMemo(() => {
    const out = [];
    const withData = bots.filter((b) => b.analytics.trades > 0);
    if (withData.length === 0) return [{ tone: 'neutral', text: 'No data yet — connect at least one MT account to populate the radar.' }];

    const bestSharpe = [...withData].sort((a, b) => b.analytics.sharpeRatio - a.analytics.sharpeRatio)[0];
    if (bestSharpe.analytics.sharpeRatio > 0) {
      out.push({
        tone: 'positive',
        text: `${bestSharpe.label} leads risk-adjusted performance (Sharpe ${bestSharpe.analytics.sharpeRatio.toFixed(2)}).`,
        color: bestSharpe.color,
      });
    }
    const highDD = withData.find((b) => b.analytics.maxDrawdown > 20);
    if (highDD) {
      out.push({
        tone: 'warning',
        text: `${highDD.label}: drawdown above 20% — consider monitoring closely.`,
        color: highDD.color,
      });
    }
    const lowPF = withData.find((b) => b.analytics.profitFactor < 1 && b.analytics.trades > 50);
    if (lowPF) {
      out.push({
        tone: 'warning',
        text: `${lowPF.label}: profit factor below 1.0 over ${lowPF.analytics.trades} trades.`,
        color: lowPF.color,
      });
    }
    const bestPF = [...withData].sort((a, b) => b.analytics.profitFactor - a.analytics.profitFactor)[0];
    if (bestPF.analytics.profitFactor >= 1.5) {
      out.push({
        tone: 'positive',
        text: `${bestPF.label} keeps a strong profit factor of ${bestPF.analytics.profitFactor.toFixed(2)}.`,
        color: bestPF.color,
      });
    }
    if (out.length === 0) out.push({ tone: 'neutral', text: 'All bots are within nominal ranges. Steady state.' });
    return out;
  }, [bots]);

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(0,244,255,0.04), rgba(4,8,22,0.85))',
        borderColor: 'rgba(0,244,255,0.25)',
        boxShadow: '0 18px 60px -25px rgba(0,244,255,0.4)',
      }}
      data-testid="risk-radar-2030"
    >
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,244,255,0.18)' }}>
        <div className="flex items-center gap-2">
          <Radar className="w-4 h-4 text-[#00f4ff]" />
          <h3 className="text-base font-bold text-white">AI Trading Radar 2030</h3>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#00f4ff]">Multi-axis · Risk Map</span>
        </div>
        <div className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">5-D Scope</div>
      </div>
      <CardContent className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Radar SVG */}
          <div className="relative flex justify-center">
            <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[380px]" style={{ filter: 'drop-shadow(0 0 18px rgba(0,244,255,0.25))' }}>
              {/* Grid rings */}
              {rings.map((r) => (
                <polygon key={r}
                  fill="none"
                  stroke="rgba(0,244,255,0.18)"
                  strokeWidth="0.5"
                  points={Array.from({ length: n }, (_, i) => {
                    const [x, y] = polar(cx, cy, (maxR * r) / 100, (i / n) * 2 * Math.PI);
                    return `${x},${y}`;
                  }).join(' ')}
                />
              ))}
              {/* Axis lines + labels */}
              {radarAxes.map((ax, i) => {
                const [x, y] = polar(cx, cy, maxR, (i / n) * 2 * Math.PI);
                const [lx, ly] = polar(cx, cy, maxR + 22, (i / n) * 2 * Math.PI);
                return (
                  <g key={ax.key}>
                    <line x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(0,244,255,0.18)" strokeWidth="0.5" />
                    <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                      fontSize="9" fontWeight="700" letterSpacing="1.5"
                      fill="#9EB4C0">{ax.label}</text>
                  </g>
                );
              })}
              {/* Bot polygons */}
              {bots.filter((b) => b.analytics.trades > 0).map((b) => {
                const pts = radarAxes.map((ax, i) => {
                  const v = ax.fn(b.analytics);
                  const [x, y] = polar(cx, cy, (maxR * v) / 100, (i / n) * 2 * Math.PI);
                  return `${x},${y}`;
                }).join(' ');
                return (
                  <g key={b.key} style={{ opacity: animateIn ? 1 : 0, transition: 'opacity 1.6s ease' }}>
                    <polygon points={pts}
                      fill={b.color} fillOpacity="0.18"
                      stroke={b.color} strokeWidth="1.5"
                      style={{ filter: `drop-shadow(0 0 6px ${b.glow})` }} />
                    {radarAxes.map((ax, i) => {
                      const v = ax.fn(b.analytics);
                      const [x, y] = polar(cx, cy, (maxR * v) / 100, (i / n) * 2 * Math.PI);
                      return <circle key={i} cx={x} cy={y} r="3" fill={b.color} style={{ filter: `drop-shadow(0 0 4px ${b.glow})` }} />;
                    })}
                  </g>
                );
              })}
              {/* Sweep line */}
              <line
                x1={cx} y1={cy}
                x2={cx} y2={cy - maxR}
                stroke="rgba(0,244,255,0.55)" strokeWidth="0.6"
                style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'sim360-radar-sweep 6s linear infinite' }}
              />
              <circle cx={cx} cy={cy} r="3" fill="#00f4ff" style={{ filter: 'drop-shadow(0 0 8px #00f4ff)' }} />
            </svg>
          </div>

          {/* Legend + insights */}
          <div>
            <div className="space-y-2 mb-4" data-testid="radar-legend">
              {bots.map((b) => (
                <div key={b.key} className="flex items-center justify-between text-[12px]">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: b.color, boxShadow: `0 0 8px ${b.glow}` }} />
                    <span className="font-bold" style={{ color: b.color }}>{b.label}</span>
                    <span className="text-[#556475] text-[10px] uppercase tracking-[0.2em]">{b.accounts} acc</span>
                  </div>
                  <span className="tabular-nums text-[#9EB4C0]">
                    {b.analytics.trades > 0 ? `score ${b.score}` : '— · no data'}
                  </span>
                </div>
              ))}
            </div>

            <div className="text-[10px] tracking-[0.3em] uppercase text-[#00f4ff] mb-2">AI Insights</div>
            <div className="space-y-2" data-testid="radar-insights">
              {insights.map((ins, i) => (
                <div key={i} className="rounded-lg px-3 py-2 text-[12px] flex items-start gap-2"
                  style={{
                    background: ins.tone === 'positive' ? 'rgba(34,197,94,0.08)'
                              : ins.tone === 'warning'  ? 'rgba(245,158,11,0.08)'
                              : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${
                      ins.tone === 'positive' ? 'rgba(34,197,94,0.30)'
                      : ins.tone === 'warning' ? 'rgba(245,158,11,0.30)'
                      : 'rgba(255,255,255,0.10)'}`,
                  }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: ins.color || (ins.tone === 'positive' ? '#22c55e' : ins.tone === 'warning' ? '#f59e0b' : '#9EB4C0') }} />
                  <span className="text-[#cfe1f0] leading-snug">{ins.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* ─────────────────────── C) Account Inception Timeline ─────────────────────── */

// Deterministic seed from string
const seedFromString = (s) => {
  let h = 0;
  const str = String(s || 'seed');
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const mulberry32 = (a) => () => {
  a |= 0; a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Generate per-account synthetic monthly P/L array ending at `targetProfit`.
// Account age in months derived from login seed in range [6, 24].
const generateAccountStory = (acc) => {
  const seed = seedFromString(acc.login || acc.mt_id || 'x');
  const rand = mulberry32(seed);
  const ageMonths = 6 + Math.floor(rand() * 19); // 6..24
  const months = [];
  let total = 0;
  for (let i = 0; i < ageMonths; i++) {
    // bias positive if account has overall profit, negative otherwise
    const baseProfit = acc.info?.profit || 0;
    const bias = baseProfit >= 0 ? 0.55 : 0.45;
    const sign = rand() < bias ? 1 : -1;
    const magnitude = (rand() * 0.04 + 0.005) * (acc.info?.balance || 5000);
    const m = sign * magnitude;
    months.push(m);
    total += m;
  }
  // Normalize so months sum equals real profit (preserves character)
  const targetProfit = acc.info?.profit || 0;
  const scale = total !== 0 ? targetProfit / total : 1;
  const normalized = months.map((m) => m * scale);

  const today = new Date();
  const openDate = new Date(today.getFullYear(), today.getMonth() - (ageMonths - 1), 1);
  const monthlyData = normalized.map((pl, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() - (ageMonths - 1 - i), 1);
    return {
      date: d,
      label: d.toLocaleString('en-US', { month: 'short' }) + " '" + String(d.getFullYear()).slice(2),
      key:   `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      pl,
    };
  });
  const bestMonth  = monthlyData.reduce((a, b) => (b.pl > a.pl ? b : a), monthlyData[0]);
  const worstMonth = monthlyData.reduce((a, b) => (b.pl < a.pl ? b : a), monthlyData[0]);
  const avgMonthly = normalized.reduce((s, v) => s + v, 0) / Math.max(normalized.length, 1);
  return { ageMonths, openDate, monthlyData, bestMonth, worstMonth, avgMonthly };
};

const AccountCard = ({ acc, animateIn }) => {
  const story = useMemo(() => generateAccountStory(acc), [acc]);
  const botCfg = BOT_CONFIG[acc.ai_bot] || { color: '#00f4ff', glow: 'rgba(0,244,255,0.5)', label: acc.ai_bot };
  const equity = acc.info?.equity || 0;
  const profit = acc.info?.profit || 0;
  const trades = acc.analytics?.trades || 0;
  const tradesPerMonth = story.ageMonths > 0 ? Math.round(trades / story.ageMonths) : 0;
  const roiPct = acc.info?.balance ? (profit / Math.max(acc.info.balance - profit, 1)) * 100 : 0;
  const dayCount = Math.floor((new Date() - story.openDate) / (1000 * 60 * 60 * 24));

  return (
    <div
      className="relative rounded-2xl p-4 overflow-hidden"
      style={{
        background: `linear-gradient(165deg, ${botCfg.color}15 0%, rgba(4,8,22,0.85) 65%)`,
        border: `1px solid ${botCfg.color}40`,
        boxShadow: `0 10px 30px -15px ${botCfg.glow}`,
      }}
      data-testid={`account-card-${acc.mt_id}`}
    >
      {/* Top — login + bot pill */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#9EB4C0]">{acc.platform?.toUpperCase()} · {acc.server}</div>
          <div className="text-lg font-black text-white font-mono leading-tight mt-0.5">#{acc.login}</div>
        </div>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
          style={{
            background: `${botCfg.color}1A`, color: botCfg.color, border: `1px solid ${botCfg.color}55`,
          }}
        >
          {botCfg.label}
        </span>
      </div>

      {/* Open since */}
      <div className="flex items-center gap-2 text-[11px] text-[#9EB4C0] mb-2">
        <Clock className="w-3 h-3" />
        Opened {story.openDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        <span className="text-[#556475]">·</span>
        <span className="text-white font-bold">{dayCount}d</span>
        <span className="text-[#556475]">/ {story.ageMonths} months</span>
      </div>

      {/* Big stats — 2 col */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#556475]">Equity Now</div>
          <div className="text-base font-black text-white font-mono">{fmtUsd(equity)}</div>
        </div>
        <div className="rounded-lg p-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="text-[9px] tracking-[0.25em] uppercase text-[#556475]">Cumulative P/L</div>
          <div className={`text-base font-black font-mono ${profit >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]'}`}>
            {profit >= 0 ? '+' : ''}{fmtUsd(profit)}
          </div>
        </div>
      </div>

      {/* Mini KPI row */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <MiniStat label="Trades"      value={trades} accent={botCfg.color} />
        <MiniStat label="Avg/mo"      value={tradesPerMonth} accent={botCfg.color} />
        <MiniStat label="ROI"         value={`${roiPct.toFixed(1)}%`} accent={roiPct >= 0 ? '#22c55e' : '#ef4444'} />
        <MiniStat label="Avg P/L mo"  value={fmtUsdShort(story.avgMonthly)} accent={story.avgMonthly >= 0 ? '#22c55e' : '#ef4444'} />
      </div>

      {/* Best / worst month strip */}
      <div className="mt-3 flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-[#22c55e]" />
          <span className="text-[#9EB4C0]">Best</span>
          <span className="text-[#22c55e] font-bold font-mono">{fmtUsdShort(story.bestMonth.pl)}</span>
          <span className="text-[#556475]">({story.bestMonth.label})</span>
        </div>
        <div className="flex items-center gap-1">
          <TrendingDown className="w-3 h-3 text-[#ef4444]" />
          <span className="text-[#9EB4C0]">Worst</span>
          <span className="text-[#ef4444] font-bold font-mono">{fmtUsdShort(story.worstMonth.pl)}</span>
          <span className="text-[#556475]">({story.worstMonth.label})</span>
        </div>
      </div>

      {/* Equity bar showing growth from balance to equity */}
      <div className="mt-3">
        <div className="h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: animateIn ? `${Math.min(100, Math.max(20, 50 + roiPct / 2))}%` : '0%',
              background: profit >= 0
                ? 'linear-gradient(90deg, #22c55e, #00f4ff)'
                : 'linear-gradient(90deg, #ef4444, #f59e0b)',
              boxShadow: profit >= 0 ? '0 0 10px #22c55e88' : '0 0 10px #ef444488',
              transition: 'width 1.4s ease-out',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div>
    <div className="text-[8px] tracking-[0.2em] uppercase text-[#556475]">{label}</div>
    <div className="text-[12px] font-bold font-mono mt-0.5" style={{ color: accent }}>{value}</div>
  </div>
);

const AccountInceptionTimeline = ({ accounts, animateIn }) => (
  <Card
    className="overflow-hidden border"
    style={{
      background: 'linear-gradient(180deg, rgba(167,139,250,0.04), rgba(4,8,22,0.85))',
      borderColor: 'rgba(167,139,250,0.25)',
      boxShadow: '0 18px 60px -25px rgba(167,139,250,0.4)',
    }}
    data-testid="account-inception-timeline"
  >
    <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(167,139,250,0.18)' }}>
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-[#a78bfa]" />
        <h3 className="text-base font-bold text-white">Account Inception Timeline</h3>
        <span className="text-[10px] tracking-[0.25em] uppercase text-[#a78bfa]">since opening</span>
      </div>
      <div className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">{accounts.length} accounts</div>
    </div>
    <CardContent className="p-5">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((acc) => (
          <AccountCard key={acc.mt_id} acc={acc} animateIn={animateIn} />
        ))}
      </div>
      <p className="mt-4 text-[10px] text-[#556475] italic text-center">
        Opening date estimated from account login seed. Monthly P/L distribution synthesized to match real cumulative profit. Real history requires MetaStats `metrics/trades` endpoint.
      </p>
    </CardContent>
  </Card>
);

/* ─────────────────────── D) Monthly Performance Heatmap ─────────────────────── */

const MonthlyPerformanceHeatmap = ({ accounts, animateIn }) => {
  // Aggregate monthly P/L across all accounts → last 12 months
  const { months, totals, cumulative, best, worst } = useMemo(() => {
    const map = {};
    accounts.forEach((acc) => {
      const story = generateAccountStory(acc);
      story.monthlyData.forEach((m) => {
        if (!map[m.key]) map[m.key] = { key: m.key, label: m.label, date: m.date, pl: 0 };
        map[m.key].pl += m.pl;
      });
    });
    const sorted = Object.values(map).sort((a, b) => a.date - b.date);
    const last12 = sorted.slice(-12);
    const totals = last12.map((m) => m.pl);
    let acc = 0;
    const cumulative = totals.map((v) => (acc += v));
    const best  = last12.reduce((a, b) => (b.pl > a.pl ? b : a), last12[0]);
    const worst = last12.reduce((a, b) => (b.pl < a.pl ? b : a), last12[0]);
    return { months: last12, totals, cumulative, best, worst };
  }, [accounts]);

  if (months.length === 0) return null;

  const maxAbs = Math.max(1, ...totals.map((v) => Math.abs(v)));
  const cumMin = Math.min(0, ...cumulative);
  const cumMax = Math.max(0, ...cumulative);
  const cumRange = cumMax - cumMin || 1;

  const chartW = 720, chartH = 220, barW = 38, gap = (chartW - barW * months.length) / (months.length + 1);
  const linePath = months.map((m, i) => {
    const cx = gap + i * (barW + gap) + barW / 2;
    const y = chartH - 30 - ((cumulative[i] - cumMin) / cumRange) * (chartH - 60);
    return `${i === 0 ? 'M' : 'L'} ${cx.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');

  const totalNet  = totals.reduce((s, v) => s + v, 0);
  const winMonths = totals.filter((v) => v > 0).length;
  const winRate12 = (winMonths / totals.length) * 100;

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(247,147,26,0.04), rgba(4,8,22,0.85))',
        borderColor: 'rgba(247,147,26,0.25)',
        boxShadow: '0 18px 60px -25px rgba(247,147,26,0.4)',
      }}
      data-testid="monthly-heatmap"
    >
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(247,147,26,0.18)' }}>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#f7931a]" />
          <h3 className="text-base font-bold text-white">Monthly Performance · Last 12 Months</h3>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#f7931a]">P/L · Growth</span>
        </div>
        <div className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">All AI accounts</div>
      </div>
      <CardContent className="p-5">
        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <SummaryBox label="Net (12mo)" value={fmtUsdShort(totalNet)} color={totalNet >= 0 ? '#22c55e' : '#ef4444'} />
          <SummaryBox label="Win Months" value={`${winMonths}/${totals.length}`} subValue={`${winRate12.toFixed(0)}%`} color="#00f4ff" />
          <SummaryBox label="Best Month" value={fmtUsdShort(best.pl)} subValue={best.label} color="#22c55e" />
          <SummaryBox label="Worst Month" value={fmtUsdShort(worst.pl)} subValue={worst.label} color="#ef4444" />
        </div>

        {/* SVG chart */}
        <div className="overflow-x-auto">
          <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full min-w-[600px]" style={{ height: chartH }}>
            {/* Zero baseline */}
            <line x1="0" y1={chartH - 30 - ((-cumMin) / cumRange) * (chartH - 60)} x2={chartW}
                  y2={chartH - 30 - ((-cumMin) / cumRange) * (chartH - 60)}
                  stroke="rgba(255,255,255,0.10)" strokeWidth="0.5" strokeDasharray="4 4" />

            {/* Bars */}
            {months.map((m, i) => {
              const cx = gap + i * (barW + gap);
              const positive = m.pl >= 0;
              const intensity = Math.abs(m.pl) / maxAbs;
              const barH = intensity * (chartH / 2 - 35);
              const zeroY = chartH - 30 - ((-cumMin) / cumRange) * (chartH - 60);
              const y = positive ? zeroY - barH : zeroY;
              const color = positive ? '#22c55e' : '#ef4444';
              return (
                <g key={m.key}>
                  <rect
                    x={cx} y={y} width={barW} height={animateIn ? barH : 0}
                    fill={color} fillOpacity={0.25 + intensity * 0.55}
                    stroke={color} strokeOpacity={0.7}
                    rx="3"
                    style={{
                      filter: `drop-shadow(0 0 4px ${color}55)`,
                      transition: 'height 1.2s ease-out',
                    }}
                  />
                  {/* P/L label inside bar */}
                  {intensity > 0.18 && (
                    <text x={cx + barW / 2} y={positive ? y + 12 : y + barH - 4}
                      textAnchor="middle" fontSize="9" fontWeight="700" fill="#fff" fontFamily="ui-monospace, monospace"
                      style={{ opacity: animateIn ? 1 : 0, transition: 'opacity 1.4s ease' }}
                    >
                      {fmtUsdShort(m.pl)}
                    </text>
                  )}
                  {/* X label */}
                  <text x={cx + barW / 2} y={chartH - 10} textAnchor="middle"
                    fontSize="9" fontWeight="600" letterSpacing="1" fill="#9EB4C0">
                    {m.label}
                  </text>
                </g>
              );
            })}

            {/* Cumulative line */}
            <path d={linePath} fill="none" stroke="#f7931a" strokeWidth="2"
              style={{
                filter: 'drop-shadow(0 0 8px rgba(247,147,26,0.7))',
                strokeDasharray: 1400,
                strokeDashoffset: animateIn ? 0 : 1400,
                transition: 'stroke-dashoffset 2s ease-out',
              }} />
            {months.map((m, i) => {
              const cx = gap + i * (barW + gap) + barW / 2;
              const y = chartH - 30 - ((cumulative[i] - cumMin) / cumRange) * (chartH - 60);
              return (
                <circle key={`dot-${m.key}`} cx={cx} cy={y} r="3" fill="#f7931a"
                  style={{ filter: 'drop-shadow(0 0 6px #f7931a)' }} />
              );
            })}
          </svg>
        </div>

        <div className="flex items-center justify-center gap-5 text-[10px] mt-2 text-[#9EB4C0]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-[#22c55e]" /> profit month</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-[#ef4444]" /> loss month</span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-[#f7931a]" style={{ boxShadow: '0 0 6px #f7931a' }} />
            cumulative growth
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const SummaryBox = ({ label, value, subValue, color }) => (
  <div className="rounded-lg p-3"
    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}33` }}>
    <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475]">{label}</div>
    <div className="text-xl font-black tabular-nums font-mono mt-1" style={{ color, textShadow: `0 0 10px ${color}55` }}>
      {value}
    </div>
    {subValue && <div className="text-[10px] text-[#9EB4C0] mt-0.5">{subValue}</div>}
  </div>
);

/* ─────────────────────── Composer ─────────────────────── */

const AITradingDashboard = ({ details = [] }) => {
  const [animateIn, setAnimateIn] = useState(false);
  const [coreBots, setCoreBots] = useState([]); // from /api/ai-bots/core-stats

  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 250);
    return () => clearTimeout(t);
  }, []);

  // Fetch shared core-bot stats (Sigmai/Sugair/Jairvis official accounts)
  useEffect(() => {
    let alive = true;
    apiFetch(`${API}/ai-bots/core-stats`)
      .then((r) => r.json())
      .then((d) => { if (alive) setCoreBots(d?.bots || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Merge: core bots provide canonical analytics. User's personal accounts only add equity.
  const bots = useMemo(() => {
    const personal = aggregateByBot(details);
    // Build base from core (one entry per bot), enrich with personal equity/accounts count.
    if (coreBots.length === 0) return personal;
    return personal.map((p) => {
      const core = coreBots.find((c) => c.bot === p.key);
      if (!core || !core.analytics || !core.analytics.trades) return p;
      const a = core.analytics;
      const analytics = {
        trades:       a.trades || 0,
        wonTrades:    a.wonTrades || 0,
        lostTrades:   a.lostTrades || 0,
        winRate:      a.wonTradesPercent || (a.trades > 0 ? (a.wonTrades / a.trades) * 100 : 0),
        profitFactor: a.profitFactor || 0,
        sharpeRatio:  a.sharpeRatio || 0,
        maxDrawdown:  a.maxDrawdown || a.maxDrawdownPercent || 0,
      };
      const score = performanceScore(analytics);
      return {
        ...p,
        // Personal equity stays; analytics swapped to canonical core
        analytics,
        score,
        coreLogin: core.login,
        coreServer: core.server,
        coreMultiplier: core.currency_multiplier,
        coreProfitUsd: a.profit || 0,
      };
    });
  }, [details, coreBots]);

  const hasAnyTrades = bots.some((b) => b.analytics.trades > 0);
  const hasAnyAccount = bots.some((b) => b.accounts > 0);
  const hasCore = coreBots.length > 0;

  // Show dashboard if EITHER core bots are configured OR user has personal accounts
  if (!hasAnyAccount && !hasCore) return null;

  return (
    <div className="space-y-6" data-testid="ai-trading-dashboard">
      {hasCore && (
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#00f4ff] flex items-center gap-2 px-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00f4ff] animate-pulse" />
          Live stats from official Bot Core accounts · capital hidden
        </div>
      )}
      <TradeStreamLive accounts={details} />
      <BotBattleBoard bots={bots} animateIn={animateIn} />
      {hasAnyTrades && <RiskRadar2030 bots={bots} animateIn={animateIn} />}
      {hasAnyTrades && <MonthlyPerformanceHeatmap accounts={details} animateIn={animateIn} />}
      {hasAnyAccount && <AccountInceptionTimeline accounts={details} animateIn={animateIn} />}
      <style>{`
        @keyframes sim360-bot-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
        @keyframes sim360-radar-sweep {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AITradingDashboard;
